import {useMemo} from 'react';

import {buildCompanyMatchesFilterParamsRecord} from '../features/company/recruiting/matches/companyMatchesListFilters';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {AppConfig} from '../lib/AppConfig';
import {MatchData} from '../types/shared-recruiting-types';

import {useQueryPagination} from './useQueryPagination';
import {LocalQueryParams, useQueryParameterState} from './useQueryParameterState';

interface Filters extends LocalQueryParams {
  job?: string;
  status?: string;
}

export type BoardColumnKey = 'new' | 'contacted' | 'interview';

export interface BoardColumnData {
  matches: MatchData[];
  hasNext: boolean;
  getNext: () => void;
  isLoadingMore: boolean;
}

const getSelectedStatusesSet = (statusCsv: string | undefined): Set<string> => {
  const statuses =
    statusCsv
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) ?? [];
  return new Set(statuses);
};

const isColumnEnabled = (selectedStatuses: Set<string>, status: string): boolean => {
  if (selectedStatuses.size === 0) {
    return true;
  }
  return selectedStatuses.has(status);
};

export const useMatchesBoardColumnsSorting = (companyId: string | undefined) => {
  const {params} = useQueryParameterState<Filters>();

  const selectedStatuses = useMemo(() => getSelectedStatusesSet(params.status), [params.status]);

  const hasCompanyId = Boolean(companyId);
  const isNewEnabled = isColumnEnabled(selectedStatuses, 'accepted');
  const isContactedEnabled = isColumnEnabled(selectedStatuses, 'contacted');
  const isInterviewEnabled = isColumnEnabled(selectedStatuses, 'interview');
  const isNewQueryEnabled = hasCompanyId && isNewEnabled;
  const isContactedQueryEnabled = hasCompanyId && isContactedEnabled;
  const isInterviewQueryEnabled = hasCompanyId && isInterviewEnabled;

  const newFilters = useMemo(
    () => (isNewEnabled ? buildCompanyMatchesFilterParamsRecord(params.job, 'accepted') : undefined),
    [isNewEnabled, params.job]
  );
  const contactedFilters = useMemo(
    () => (isContactedEnabled ? buildCompanyMatchesFilterParamsRecord(params.job, 'contacted') : undefined),
    [isContactedEnabled, params.job]
  );
  const interviewFilters = useMemo(
    () => (isInterviewEnabled ? buildCompanyMatchesFilterParamsRecord(params.job, 'interview') : undefined),
    [isInterviewEnabled, params.job]
  );

  const {
    data: newMatchesData,
    hasNext: hasNextNew,
    getNext: getNextNew,
    isLoading: isLoadingNew,
    loadingInitialData: isLoadingInitialNew,
    error: newError,
  } = useQueryPagination<MatchData, Record<string, number>>({
    url: GetRoutes.companyMatches(companyId ?? ''),
    entity: 'matches-board-new',
    perPage: AppConfig.PAGINATION_PER_PAGE,
    reverseOrder: true,
    enabled: isNewQueryEnabled,
    filterParams: newFilters,
  });

  const {
    data: contactedMatchesData,
    hasNext: hasNextContacted,
    getNext: getNextContacted,
    isLoading: isLoadingContacted,
    loadingInitialData: isLoadingInitialContacted,
    error: contactedError,
  } = useQueryPagination<MatchData, Record<string, number>>({
    url: GetRoutes.companyMatches(companyId ?? ''),
    entity: 'matches-board-contacted',
    perPage: AppConfig.PAGINATION_PER_PAGE,
    reverseOrder: true,
    enabled: isContactedQueryEnabled,
    filterParams: contactedFilters,
  });

  const {
    data: interviewMatchesData,
    hasNext: hasNextInterview,
    getNext: getNextInterview,
    isLoading: isLoadingInterview,
    loadingInitialData: isLoadingInitialInterview,
    error: interviewError,
  } = useQueryPagination<MatchData, Record<string, number>>({
    url: GetRoutes.companyMatches(companyId ?? ''),
    entity: 'matches-board-interview',
    perPage: AppConfig.PAGINATION_PER_PAGE,
    reverseOrder: true,
    enabled: isInterviewQueryEnabled,
    filterParams: interviewFilters,
  });

  const columns = useMemo(
    () =>
      ({
        new: {
          matches: isNewQueryEnabled ? newMatchesData : [],
          hasNext: isNewQueryEnabled ? hasNextNew : false,
          getNext: getNextNew,
          isLoadingMore: isNewQueryEnabled ? isLoadingNew : false,
        },
        contacted: {
          matches: isContactedQueryEnabled ? contactedMatchesData : [],
          hasNext: isContactedQueryEnabled ? hasNextContacted : false,
          getNext: getNextContacted,
          isLoadingMore: isContactedQueryEnabled ? isLoadingContacted : false,
        },
        interview: {
          matches: isInterviewQueryEnabled ? interviewMatchesData : [],
          hasNext: isInterviewQueryEnabled ? hasNextInterview : false,
          getNext: getNextInterview,
          isLoadingMore: isInterviewQueryEnabled ? isLoadingInterview : false,
        },
      }) as Record<BoardColumnKey, BoardColumnData>,
    [
      isNewQueryEnabled,
      newMatchesData,
      hasNextNew,
      getNextNew,
      isLoadingNew,
      isContactedQueryEnabled,
      contactedMatchesData,
      hasNextContacted,
      getNextContacted,
      isLoadingContacted,
      isInterviewQueryEnabled,
      interviewMatchesData,
      hasNextInterview,
      getNextInterview,
      isLoadingInterview,
    ]
  );

  return {
    columns,
    loadingInitialData:
      (isNewQueryEnabled && isLoadingInitialNew) ||
      (isContactedQueryEnabled && isLoadingInitialContacted) ||
      (isInterviewQueryEnabled && isLoadingInitialInterview),
    error:
      (isNewQueryEnabled && newError) ||
      (isContactedQueryEnabled && contactedError) ||
      (isInterviewQueryEnabled && interviewError),
  };
};
